import { Input, Col, Button, Row } from "reactstrap";
import FormError from "../../components/Shared/Forms/FormError";
import React, { forwardRef, useState, useEffect, useImperativeHandle, useRef } from "react";
import { FormikProps } from "formik";
import { getImageUrl } from "../../helper/getImageUrl";
import {useLazyQuery} from "@apollo/client"
import { getImageUploadURL } from "../../graphql/queries/getImageUploadURL";
import Loading from "../Loading";
import ImageLibrarySelectorModal from "./ImageLibrarySelectorModal";
import axios from "axios";
import { BlobToFile, GetExtensionFromMimeType } from "../../helper/Utilities";

// TODO : ISSUE it seems using this on formik the lazy query is running but itself when multiple items of this components are used so for now 
// we will have an option to not trigger the lazy query for getting the url and do it outside of this component

interface Props  {
    isDisabled? : boolean;
    formikRef : React.RefObject<FormikProps<any>>;
    extensionAllowed? : string; 
    alt? : string;
    transferFunction : (url : string, fileId : string, blob : File) => void;
    isGettingUrl : (isGetting : boolean) => void;
    imageName : string;
    imageIdName? : string;
    formGroupClassName? : string;
    required? :boolean;
    dontFetchUrl? : boolean; 
    label? : string;
    maxMb? : number;
 }

const ImageUploadInput =  forwardRef(function ImageUploadInput(
    { formikRef, extensionAllowed, isDisabled, alt, transferFunction, isGettingUrl,
         imageName, imageIdName, formGroupClassName, required , dontFetchUrl = false, label, maxMb = 10}: Props,
    ref: React.Ref<{ clearImageField: () => void, refetchUrl? : () => void }>
) 
{ 
    const fileInputRef = useRef<any>(null);
    const [imageTypesAllowed, setImageTypesAllowed] = useState<string>("image/png,image/gif,image/jpeg,image/jpg,image/webp,image/svg+xml")

    const [previewImage, setPreviewImage] = useState<string>(null); 
    const [blobFile, setBlobFile] = useState<File>(null);

    const { values, setFieldValue, touched, errors } = formikRef?.current  || {};
    const [validationMessage, setValidationMessage] = useState("");

    const [isImageSelectorOpen, setIsImageSelectorOpen] = useState<boolean>(false);
    const [isFetchingImage, setIsFetchingImage] = useState<boolean>(false);

    const [
        getSignedUrl,
        { loading: fetchingSignedUrl, error: fetchSignedUrlError },
    ] = useLazyQuery(getImageUploadURL, {
        fetchPolicy: "network-only",
        onCompleted({getImageUploadURL}){
            transferFunction(getImageUploadURL.uploadURL, getImageUploadURL.id, blobFile);
        },
        onError(){
            console.error('error')
        }
    });
    const fetchingSignedUrlBefore = useRef(false);
    useEffect(() => {
        if(fetchingSignedUrlBefore.current !== fetchingSignedUrl){
            fetchingSignedUrlBefore.current = fetchingSignedUrl
            isGettingUrl(fetchingSignedUrl);
        }
    },[fetchingSignedUrl,isGettingUrl])

    useEffect(() =>{
        if(extensionAllowed) {
            setImageTypesAllowed(extensionAllowed);
        }
    },[extensionAllowed]);

    // Function to expose to the parent
    const clearImageField = () => {
        setPreviewImage(null);
        setBlobFile(null);
        transferFunction(null,null,null);
    };

    const refetchUrl = () => {
        if(blobFile) {
            getSignedUrl();
        }
    };

    // Expose the child function to the parent via ref
    useImperativeHandle(ref, () => ({
        clearImageField: clearImageField,
        refetchUrl : refetchUrl
    }));
    const [reseter,setReseter] = useState<boolean>(false);
    const HandleRemoveImage = () => {
        setReseter(!reseter);
        setPreviewImage(null);
        setBlobFile(null);
        setFieldValue(imageIdName, "");
        setFieldValue(imageName, "");
        transferFunction(null,null,null);
        setValidationMessage("");
    };

    const ToggleImageSelector = () => {
        setIsImageSelectorOpen(!isImageSelectorOpen)
    }

    const GetSelectedImage = async (id : string) => {
        const getUrl = getImageUrl(id);
        isGettingUrl(true);
        setIsFetchingImage(true);
        const response = await axios.get(getUrl, { responseType: 'blob' });
        setIsFetchingImage(false);
        const blob = response.data;
        const fileName =id +"."+ GetExtensionFromMimeType(blob.type);

        const file = BlobToFile(blob,fileName);

        const imageUrl = URL.createObjectURL(blob);

        setFieldValue(imageName, fileName);
        setValidationMessage("");

        setPreviewImage(imageUrl);
        setBlobFile(file);
        isGettingUrl(false);

        if(!dontFetchUrl) {
            getSignedUrl(); 
        }
        else {
            transferFunction(null,null,file);
        }
    }

    return (
        <div className={`form-group ${formGroupClassName}`} ref={fileInputRef}>
            <label>{label || "Image"} {required ? "*" : "" }</label>
            {
                (!previewImage && !values?.[imageIdName]) && 
                <Input
                    
                    name={imageName}
                    type="file"
                    accept={imageTypesAllowed}
                    className={`form-control`}
                    disabled={fetchingSignedUrl}
                    onChange={(event) => {
                        // Use Formik's setFieldValue to set the file value
                        const file = event.currentTarget.files[0]
                        if(file)
                        {
                            const imageUrl = URL.createObjectURL(file);

                            setFieldValue(imageName, file.name);
                            setValidationMessage("");

                             // Check file type
                            if (!imageTypesAllowed.includes(file.type)) {
                                setValidationMessage("Invalid file type");
                                return;
                            }
                            
                            // Check file size (in MB)
                            const fileSizeMB = file.size / (1024 * 1024);
                            if (fileSizeMB > maxMb) {
                                setValidationMessage(`File size must be less than ${maxMb}MB`);
                                return;
                            }

                            setPreviewImage(imageUrl);
                            setBlobFile(file);
                            if(!dontFetchUrl) {
                                getSignedUrl(); 
                            }
                            else {
                                transferFunction(null,null,file);
                            }
                        }
                        else
                        {
                            setFieldValue(imageName, null);
                            setPreviewImage(null);
                            setBlobFile(null);
                            
                            transferFunction(null,null,null);
                        }
                        
                    }}
                />
            }
            {
                validationMessage &&
                <FormError>{validationMessage}</FormError>
            }
            <FormError>
                {touched?.[imageName] && typeof errors?.[imageName] === 'string' && (errors?.[imageName] as string) }
                {touched?.[imageIdName] && typeof errors?.[imageIdName] === 'string' && (errors?.[imageIdName] as string) }
            </FormError>
            {fetchSignedUrlError && (
                <FormError>Something went wrong whilst uploading image.</FormError>
            )}
            {
                (fetchingSignedUrl || isFetchingImage) ?  
                    <Loading /> 
                :
                <Row>
                    <Col>
                        {
                            (previewImage || values?.[imageIdName]) &&
                            <img
                                className="pt-3"
                                style={{
                                    width: "100%",
                                    borderRadius: "0.375rem",
                                    marginBottom: "1rem",
                                }}
                                src={previewImage || getImageUrl(values?.[imageIdName])}
                                alt={alt}
                            />
                        }
                    </Col>
                    <Col className="d-flex align-items-center">
                        {
                            (previewImage || values?.[imageIdName]) &&
                            (
                                <Button 
                                    title="Click to remove this image and to upload new image."
                                    color="secondary" 
                                    type="button" 
                                    disabled={isDisabled}
                                    onClick={HandleRemoveImage}
                                >
                                    <div>
                                        <i className="fas fa-trash-alt"></i>
                                    </div>
                                </Button>
                            )
                        }       
                    </Col>
                </Row>
                
            }
            {
                (!previewImage && !values?.[imageIdName] && !isFetchingImage) &&
                <span className="text-primary" role="button" onClick={ToggleImageSelector}>
                    Select From Library
                </span>
            }
            
            
            <ImageLibrarySelectorModal 
                getSelected={GetSelectedImage}
                modalOpen={isImageSelectorOpen}
                toggleModal={ToggleImageSelector}
            />
        </div>
    )
});

export default ImageUploadInput;